/**
 * v-adaptive-height
 *
 * A directive that dynamically sets max-height of a target element based on available space
 * in its container element.
 *
 * Usage examples:
 *
 * 1. Direct usage in template (when container and target are both in DOM):
 *    <div v-adaptive-height="{ target: '.js-player' }">...</div>
 *
 * 2. Programmatic usage (when elements may load dynamically):
 *
 *    // In component methods:
 *    setupAdaptiveHeight() {
 *      const container = document.querySelector('.container');
 *      this.$options.directives['adaptive-height'].bind(
 *        container,
 *        { value: { target: '.target' } }
 *      );
 *    }
 *
 *    // Don't forget to clean up:
 *    destroyed() {
 *      const container = document.querySelector('.container');
 *      if (container && container._adaptiveHeightConfig) {
 *        this.$options.directives['adaptive-height'].unbind(container);
 *      }
 *    }
 */

export default {
	bind(el, binding) {
		if (!binding.value || !binding.value.target) {
			console.warn('v-adaptive-height: Target selector is required');
			return;
		}

		const config = {
			targetSelector:   binding.value.target,
			observeChildren:  binding.value.observeChildren === true,
			disabled:         binding.value.disabled === true,
			resizeObserver:   null,
			mutationObserver: null,
			updateScheduled:  false,
		};

		// Store config in element for later use
		el._adaptiveHeightConfig = config;

		// Setup mutation observer to detect when target appears in DOM
		config.mutationObserver = new MutationObserver(() => {
			const targetEl = document.querySelector(config.targetSelector);
			if (targetEl) {
				setupResizeObserver(el);
				updateTargetMaxHeight(el);
				config.mutationObserver.disconnect();
			}
		});

		config.mutationObserver.observe(document.body, {
			childList: true,
			subtree:   true,
		});

		// Add window resize listener
		el._updateTargetMaxHeight = () => updateTargetMaxHeight(el);
		window.addEventListener('resize', el._updateTargetMaxHeight);

		// Initial setup if target already exists
		const targetEl = document.querySelector(config.targetSelector);
		if (targetEl) {
			setupResizeObserver(el);
			updateTargetMaxHeight(el);
		}
	},

	unbind(el) {
		// Clean up all observers and event listeners
		const config = el._adaptiveHeightConfig;
		if (!config) return;

		if (config.resizeObserver) {
			config.resizeObserver.disconnect();
		}

		if (config.mutationObserver) {
			config.mutationObserver.disconnect();
		}

		window.removeEventListener('resize', el._updateTargetMaxHeight);
		delete el._adaptiveHeightConfig;
		delete el._updateTargetMaxHeight;
	},

	update(el, binding) {
		const config = el._adaptiveHeightConfig;
		if (!config) return;

		config.disabled = binding.value?.disabled === true;
		updateTargetMaxHeight(el);
	},
};

/**
 * Set up ResizeObserver to monitor container size changes
 */
function setupResizeObserver(el) {
	const config = el._adaptiveHeightConfig;
	if (!config || config.resizeObserver) return;

	config.resizeObserver = new ResizeObserver(() => {
		// Use requestAnimationFrame to avoid multiple updates in same frame
		if (!config.updateScheduled) {
			config.updateScheduled = true;
			window.requestAnimationFrame(() => {
				updateTargetMaxHeight(el);
				config.updateScheduled = false;
			});
		}
	});

	config.resizeObserver.observe(el);
	if (config.observeChildren) {
		Array.from(el.children).forEach(child => config.resizeObserver.observe(child));
	}
}

/**
 * Calculate and set max-height of target element
 */
function updateTargetMaxHeight(el) {
	const config = el._adaptiveHeightConfig;
	if (!config) return;

	const targetEl = document.querySelector(config.targetSelector);
	if (!el || !targetEl) return;

	if (config.disabled) {
		targetEl.style.removeProperty('max-height');
		targetEl.style.removeProperty('--adaptive-max-height');
		return;
	}

	// Store original flex-shrink values and temporarily set to 0
	const children = Array.from(el.children);
	const originalFlexShrink = [];
	children.forEach(child => {
		originalFlexShrink.push(child.style.flexShrink);
		child.style.flexShrink = '0';
	});

	// Force a reflow to ensure new styles are applied
	el.getBoundingClientRect();

	const containerHeight = el.offsetHeight;
	const containerContentHeight = children.reduce(
		(total, child) => total + child.offsetHeight,
		0
	);

	// Restore original flex-shrink values
	children.forEach((child, index) => {
		if (originalFlexShrink[index]) {
			child.style.flexShrink = originalFlexShrink[index];
		} else {
			child.style.removeProperty('flex-shrink');
		}
	});

	const maxHeight = containerHeight - (containerContentHeight - targetEl.offsetHeight);
	targetEl.style.maxHeight = `${maxHeight}px`;
	targetEl.style.setProperty('--adaptive-max-height', `${maxHeight}px`);
}
